import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const EMPTY = { company_name: "", division_name: "Main Division", admin_name: "", admin_email: "", admin_password: "" };
const FIELDS = [
  ["company_name", "Company name", "text"],
  ["division_name", "First division", "text"],
  ["admin_name", "Admin full name", "text"],
  ["admin_email", "Admin email", "email"],
  ["admin_password", "Admin password (min 6)", "password"],
];

export const CreateTenantDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (body) => api.post("/superadmin/companies", body).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Tenant "${data.company.name}" created — admin ${data.admin.email} can now log in`);
      qc.invalidateQueries({ queryKey: ["sa-companies"] });
      setForm(EMPTY);
      setOpen(false);
      onCreated?.(data.company.id);
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.toString?.() || "Could not create tenant"),
  });
  const submit = (e) => { e.preventDefault(); m.mutate(form); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm" data-testid="create-tenant-btn"><Plus className="h-4 w-4 mr-1" /> New Tenant</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm" data-testid="create-tenant-dialog">
        <DialogHeader><DialogTitle className="font-display">Create tenant company</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {FIELDS.map(([k, label, type]) => (
            <div key={k} className="space-y-1">
              <Label htmlFor={`tenant-${k}`}>{label}</Label>
              <Input id={`tenant-${k}`} type={type} required minLength={k === "admin_password" ? 6 : undefined} className="rounded-sm" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`tenant-${k.replace(/_/g, "-")}-input`} />
            </div>
          ))}
          <DialogFooter>
            <Button type="submit" className="rounded-sm" disabled={m.isPending} data-testid="create-tenant-submit">{m.isPending ? "Creating…" : "Create tenant"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
